import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BuffOrDebuff } from '../../types';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { formatBuffEffect, getBuffName } from '../../utils/formatting';
import { CustomContentManager } from '../shared/CustomContentManager';

type Scope = 'member' | 'you' | 'them' | 'both';
type ChangeType = '' | 'add' | 'set';
type StatKey = 'move' | 'fight' | 'shoot' | 'armour' | 'will' | 'health';

interface StatFormValue {
  type: ChangeType;
  value: string;
  minimum: string;
  maximum: string;
}

interface EffectForm {
  name: string;
  scope: Scope;
  stacking: string;
  stats: Record<StatKey, StatFormValue>;
  diceRollType: string;
  diceRollValue: string;
}

const STATS: Array<{ key: StatKey; label: string; supportsBounds: boolean }> = [
  { key: 'move', label: 'Move', supportsBounds: true },
  { key: 'fight', label: 'Fight', supportsBounds: true },
  { key: 'shoot', label: 'Shoot', supportsBounds: true },
  { key: 'armour', label: 'Armour', supportsBounds: true },
  { key: 'will', label: 'Will', supportsBounds: true },
  { key: 'health', label: 'Health', supportsBounds: false },
];

const emptyStat = (): StatFormValue => ({ type: '', value: '', minimum: '', maximum: '' });

const emptyForm = (): EffectForm => ({
  name: '',
  scope: 'member',
  stacking: '0',
  stats: {
    move: emptyStat(),
    fight: emptyStat(),
    shoot: emptyStat(),
    armour: emptyStat(),
    will: emptyStat(),
    health: emptyStat(),
  },
  diceRollType: '',
  diceRollValue: '',
});

const numberOrNull = (value: string): number | null => value === '' ? null : Number(value);

function formFromEffect(effect: BuffOrDebuff): EffectForm {
  const statValue = (key: StatKey): StatFormValue => ({
    type: (effect[`${key}ChangeType` as keyof BuffOrDebuff] as ChangeType | null) ?? '',
    value: String(effect[`${key}ChangeValue` as keyof BuffOrDebuff] ?? ''),
    minimum: key === 'health' ? '' : String(effect[`${key}Minimum` as keyof BuffOrDebuff] ?? ''),
    maximum: key === 'health' ? '' : String(effect[`${key}Maximum` as keyof BuffOrDebuff] ?? ''),
  });

  const scope: Scope = effect.globalYou > 0 && effect.globalThem > 0
    ? 'both'
    : effect.globalYou > 0
      ? 'you'
      : effect.globalThem > 0
        ? 'them'
        : 'member';

  return {
    name: getBuffName(effect),
    scope,
    stacking: String(effect.stacking),
    stats: {
      move: statValue('move'),
      fight: statValue('fight'),
      shoot: statValue('shoot'),
      armour: statValue('armour'),
      will: statValue('will'),
      health: statValue('health'),
    },
    diceRollType: effect.diceRollChangeType ?? '',
    diceRollValue: String(effect.diceRollChangeValue ?? ''),
  };
}

function effectFromForm(form: EffectForm): Omit<BuffOrDebuff, 'id' | 'isCustom'> {
  const result: Omit<BuffOrDebuff, 'id' | 'isCustom'> = {
    name: form.name.trim(),
    source: 'custom',
    sourceId: null,
    globalYou: form.scope === 'you' || form.scope === 'both' ? 1 : 0,
    globalThem: form.scope === 'them' || form.scope === 'both' ? 1 : 0,
    stacking: Math.max(0, Number(form.stacking) || 0),
    moveChangeType: null,
    moveChangeValue: null,
    moveMinimum: null,
    moveMaximum: null,
    fightChangeType: null,
    fightChangeValue: null,
    fightMinimum: null,
    fightMaximum: null,
    shootChangeType: null,
    shootChangeValue: null,
    shootMinimum: null,
    shootMaximum: null,
    armourChangeType: null,
    armourChangeValue: null,
    armourMinimum: null,
    armourMaximum: null,
    willChangeType: null,
    willChangeValue: null,
    willMinimum: null,
    willMaximum: null,
    healthChangeType: null,
    healthChangeValue: null,
    diceRollChangeType: form.diceRollType.trim() || null,
    diceRollChangeValue: numberOrNull(form.diceRollValue),
  };

  for (const { key, supportsBounds } of STATS) {
    const stat = form.stats[key];
    (result as Record<string, unknown>)[`${key}ChangeType`] = stat.type || null;
    (result as Record<string, unknown>)[`${key}ChangeValue`] = numberOrNull(stat.value);
    if (supportsBounds) {
      (result as Record<string, unknown>)[`${key}Minimum`] = numberOrNull(stat.minimum);
      (result as Record<string, unknown>)[`${key}Maximum`] = numberOrNull(stat.maximum);
    }
  }

  return result;
}

const EffectFormFields: React.FC<{
  form: EffectForm;
  setForm: React.Dispatch<React.SetStateAction<EffectForm>>;
}> = ({ form, setForm }) => {
  const updateStat = (key: StatKey, patch: Partial<StatFormValue>) => {
    setForm((current) => ({
      ...current,
      stats: { ...current.stats, [key]: { ...current.stats[key], ...patch } },
    }));
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label="Name" required fullWidth value={form.name} onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} />
        <TextField select label="Applies to" fullWidth value={form.scope} onChange={(event) => setForm((f) => ({ ...f, scope: event.target.value as Scope }))}>
          <MenuItem value="member">One member</MenuItem>
          <MenuItem value="you">Your warband</MenuItem>
          <MenuItem value="them">Opposing warband</MenuItem>
          <MenuItem value="both">Both warbands</MenuItem>
        </TextField>
        <TextField label="Maximum stacks" type="number" inputProps={{ min: 0 }} value={form.stacking} onChange={(event) => setForm((f) => ({ ...f, stacking: event.target.value }))} helperText="0 means no stacking" />
      </Stack>

      <Typography variant="subtitle2">Stat modifiers</Typography>
      {STATS.map(({ key, label, supportsBounds }) => (
        <Box key={key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: supportsBounds ? '1.2fr 1fr 1fr 1fr' : '1.2fr 1fr' }, gap: 1 }}>
          <TextField select size="small" label={label} value={form.stats[key].type} onChange={(event) => updateStat(key, { type: event.target.value as ChangeType })}>
            <MenuItem value="">No change</MenuItem>
            <MenuItem value="add">Add</MenuItem>
            <MenuItem value="set">Set to</MenuItem>
          </TextField>
          <TextField size="small" label="Value" type="number" disabled={!form.stats[key].type} value={form.stats[key].value} onChange={(event) => updateStat(key, { value: event.target.value })} />
          {supportsBounds && <TextField size="small" label="Minimum" type="number" value={form.stats[key].minimum} onChange={(event) => updateStat(key, { minimum: event.target.value })} />}
          {supportsBounds && <TextField size="small" label="Maximum" type="number" value={form.stats[key].maximum} onChange={(event) => updateStat(key, { maximum: event.target.value })} />}
        </Box>
      ))}

      <Typography variant="subtitle2">Dice-roll modifier</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label="Roll type" fullWidth placeholder="e.g. initiative" value={form.diceRollType} onChange={(event) => setForm((f) => ({ ...f, diceRollType: event.target.value }))} />
        <TextField label="Modifier" type="number" fullWidth value={form.diceRollValue} onChange={(event) => setForm((f) => ({ ...f, diceRollValue: event.target.value }))} />
      </Stack>
    </Stack>
  );
};

export const ManageCustomBuffsModal: React.FC = () => {
  const { ui, closeCustomBuffsModal } = useWarbandStore();
  const { customBuffs, addCustomBuff, updateCustomBuff, deleteCustomBuff } = useReferenceDataStore();

  return (
    <Dialog open={ui.customBuffsModalOpen} onClose={closeCustomBuffsModal} maxWidth="md" fullWidth>
      <DialogTitle>Custom Buffs & Debuffs</DialogTitle>
      <DialogContent dividers>
        {ui.customBuffsModalOpen && <CustomContentManager<BuffOrDebuff, EffectForm>
          itemLabel="Effect"
          items={customBuffs}
          getItemName={getBuffName}
          getItemSecondary={formatBuffEffect}
          emptyForm={emptyForm}
          itemToForm={formFromEffect}
          validate={(form) => {
            if (!form.name.trim()) return 'Enter a name for this effect.';
            const hasModifier = STATS.some(({ key }) => form.stats[key].value !== '') || form.diceRollValue !== '';
            if (!hasModifier) return 'Add at least one stat or dice-roll modifier.';
            return null;
          }}
          renderFormFields={(form, setForm) => <EffectFormFields form={form} setForm={setForm} />}
          onSave={(form, editingId) => {
            const data = effectFromForm(form);
            if (editingId) updateCustomBuff(editingId, data);
            else addCustomBuff(data);
          }}
          onDelete={deleteCustomBuff}
          deleteMessage={(effect) => `Delete "${getBuffName(effect)}"? Active uses of this effect will no longer be available after a warband reload.`}
        />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomBuffsModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};